import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { vendorService } from '../../../services/api';
import DateRangePicker from '../../../components/DateRangePicker';
import { Eye, EyeOff, ChevronDown, RotateCw, XCircle, AlertTriangle, RefreshCw } from 'lucide-react';

const getLeadStatusColorClass = (status) => {
  const s = (status || 'New').toLowerCase();
  switch (s) {
    case 'new':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'hot':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'warm':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'cold':
      return 'bg-slate-50 text-slate-655 border-slate-200';
    case 'lost':
      return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'architect/designer':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    default:
      return 'bg-blue-50 text-blue-700 border-blue-200';
  }
};

const formatDate = (isoStr) => {
  if (!isoStr) return 'N/A';
  try {
    return new Date(isoStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (e) {
    return isoStr;
  }
};

const maskPhoneNumber = (phone) => {
  if (!phone || phone === 'N/A') return 'N/A';
  const cleanPhone = phone.trim();
  if (cleanPhone.length < 4) return cleanPhone;
  return '••••••' + cleanPhone.slice(-4);
};

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(0);

    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);

    if (currentPage <= 1) {
      end = 3;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 4;
    }

    if (start > 1) {
      pages.push('ellipsis1');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) {
      pages.push('ellipsis2');
    }

    pages.push(totalPages - 1);
  }
  return pages;
};

const MembershipAuditTab = ({ toast }) => {
  const { hasPermission } = useAuth();

  // Filters State
  const [membershipFilters, setMembershipFilters] = useState({ startDate: '', endDate: '' });
  const [selectedListingTypeFilter, setSelectedListingTypeFilter] = useState('ALL'); // 'ALL', 'COMPANY', 'INDIVIDUAL'
  const [membershipCategory, setMembershipCategory] = useState('paid'); // 'paid', 'free', 'expired'
  const [selectedPaidFilter, setSelectedPaidFilter] = useState('ALL'); // 'ALL', 'PROFESSIONAL', 'PREMIUM', 'YET_TO_EXPIRE'
  
  // Pagination State
  const [auditPage, setAuditPageState] = useState(() => {
    try {
      const saved = sessionStorage.getItem('admin_page_memberships_audit');
      return saved ? Math.max(0, parseInt(saved, 10)) : 0;
    } catch (e) {
      return 0;
    }
  });

  const setAuditPage = useCallback((valOrFn) => {
    setAuditPageState(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      try {
        sessionStorage.setItem('admin_page_memberships_audit', String(next));
      } catch (e) {}
      return next;
    });
  }, []);

  const [auditPageSize, setAuditPageSize] = useState(25);

  // Data State
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // UI Interactive State
  const [revealedAuditPhone, setRevealedAuditPhone] = useState(null);
  const [cancellingMembership, setCancellingMembership] = useState(null);
  const [cancellingLoading, setCancellingLoading] = useState(false);

  const fetchMemberships = useCallback(async () => {
    if (!hasPermission('MANAGE_MEMBERSHIPS')) return;
    setLoading(true);
    setError('');
    try {
      const res = await vendorService.getAdminMemberships();
      if (res.success && res.data) {
        setMemberships(res.data.memberships || []);
      } else {
        setError(res.error || 'Failed to fetch memberships registry.');
      }
    } catch (err) {
      setError('An error occurred while fetching memberships.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [hasPermission]);

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  const handleLeadStatusChange = async (vendorEmail, newStatus) => {
    try {
      const res = await vendorService.updateLeadStatus(vendorEmail, newStatus);
      if (res.success) {
        toast.success(`Successfully updated lead status to ${newStatus}`);
        setMemberships(prevMemberships =>
          prevMemberships.map(m =>
            m.vendor_email === vendorEmail ? { ...m, lead_status: newStatus } : m
          )
        );
      } else {
        toast.error(res.error || 'Failed to update lead status');
      }
    } catch (err) {
      toast.error('An error occurred while updating lead status');
      console.error(err);
    }
  };

  const handleConfirmCancelAutoPay = async () => {
    if (!cancellingMembership) return;
    setCancellingLoading(true);
    try {
      const res = await vendorService.cancelAdminMembershipAutoPay(cancellingMembership.id);
      if (res.success) {
        toast.success(res.message || 'AutoPay cancelled successfully for vendor membership.');
        setCancellingMembership(null);
        fetchMemberships();
      } else {
        toast.error(res.error || 'Failed to cancel AutoPay subscription.');
      }
    } catch (err) {
      console.error('Error cancelling membership AutoPay:', err);
      toast.error(err?.message || err?.detail || 'Failed to cancel AutoPay subscription.');
    } finally {
      setCancellingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center py-24 select-none">
        <div className="w-9 h-9 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-sm text-gray-500 font-semibold mt-3">Loading memberships...</span>
      </div>
    );
  }

  // Filtered by listing type and date range (Includes both Company and Individual)
  const filteredMembershipsByDate = memberships.filter(item => {
    const type = (item.listing_type || item.listingType || 'COMPANY').toUpperCase();
    if (selectedListingTypeFilter === 'COMPANY' && type === 'INDIVIDUAL') return false;
    if (selectedListingTypeFilter === 'INDIVIDUAL' && type !== 'INDIVIDUAL') return false;

    if (!item.created_at) return true;
    const itemDateStr = item.created_at.substring(0, 10);
    if (membershipFilters.startDate && itemDateStr < membershipFilters.startDate) return false;
    if (membershipFilters.endDate && itemDateStr > membershipFilters.endDate) return false;
    return true;
  });

  // Group memberships into Paid vs Free vs Expired
  const paidMemberships = filteredMembershipsByDate.filter(m => m.plan_name !== 'FREE' && m.status !== 'EXPIRED');
  const freeMemberships = filteredMembershipsByDate.filter(m => m.plan_name === 'FREE' && m.status !== 'EXPIRED');
  const expiredMemberships = filteredMembershipsByDate.filter(m => m.status === 'EXPIRED');

  // Filter Paid memberships by selectedPaidFilter
  const filteredPaidMemberships = paidMemberships.filter(m => {
    if (selectedPaidFilter === 'ALL') return true;
    if (selectedPaidFilter === 'PROFESSIONAL') return m.plan_name === 'PROFESSIONAL';
    if (selectedPaidFilter === 'PREMIUM') return m.plan_name === 'PREMIUM';
    if (selectedPaidFilter === 'YET_TO_EXPIRE') return m.status === 'YET_TO_EXPIRE';
    return true;
  });

  let activeList = [];
  if (membershipCategory === 'paid') {
    activeList = filteredPaidMemberships;
  } else if (membershipCategory === 'free') {
    activeList = freeMemberships;
  } else if (membershipCategory === 'expired') {
    activeList = expiredMemberships;
  }

  const auditTotalPages = Math.ceil(activeList.length / auditPageSize);
  const safeAuditPage = Math.min(auditPage, Math.max(0, auditTotalPages - 1));
  const displayedAuditList = activeList.slice(safeAuditPage * auditPageSize, (safeAuditPage + 1) * auditPageSize);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-left">
      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200/50 p-4 rounded-xl mb-6 flex items-center gap-3 shadow-sm">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {/* Tab Title & Filters */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Memberships & Sales Audit</h2>
        </div>
        
        {/* Filters: Listing Type & Date Filter */}
        <div className="flex flex-wrap items-center gap-3 bg-gray-50 border border-gray-100 p-3 rounded-xl select-none">
          {/* Listing Type Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Type:</span>
            <select
              value={selectedListingTypeFilter}
              onChange={(e) => {
                setSelectedListingTypeFilter(e.target.value);
                setAuditPage(0);
              }}
              className="bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] cursor-pointer"
            >
              <option value="ALL">All Types</option>
              <option value="COMPANY">Company</option>
              <option value="INDIVIDUAL">Individual</option>
            </select>
          </div>

          <div className="h-4 w-[1px] bg-gray-200 hidden sm:block"></div>

          {/* Date Filter Inputs */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Date:</span>
            <DateRangePicker
              startDate={membershipFilters.startDate}
              endDate={membershipFilters.endDate}
              onChange={(start, end) => {
                setMembershipFilters(prev => ({ ...prev, startDate: start, endDate: end }));
                setAuditPage(0);
              }}
              placeholder="Select Date Range"
            />
          </div>
          {(membershipFilters.startDate || membershipFilters.endDate || selectedListingTypeFilter !== 'ALL') && (
            <button
              onClick={() => {
                setMembershipFilters({ startDate: '', endDate: '' });
                setSelectedListingTypeFilter('ALL');
                setAuditPage(0);
              }}
              className="px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 border border-red-200/50 rounded-lg transition-colors cursor-pointer"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Three Categories (Paid vs Free vs Expired) */}
      <div className="flex border-b border-gray-200 mb-6 gap-6 text-sm font-semibold select-none">
        <button
          className={`pb-3 cursor-pointer transition-all ${membershipCategory === 'paid' ? 'text-[#2ab391] border-b-2 border-[#2ab391]' : 'text-gray-500 hover:text-gray-800'}`}
          onClick={() => {
            setMembershipCategory('paid');
            setAuditPage(0);
          }}
        >
          Paid Plans ({paidMemberships.length})
        </button>
        <button
          className={`pb-3 cursor-pointer transition-all ${membershipCategory === 'free' ? 'text-[#2ab391] border-b-2 border-[#2ab391]' : 'text-gray-500 hover:text-gray-800'}`}
          onClick={() => {
            setMembershipCategory('free');
            setAuditPage(0);
          }}
        >
          Free Plans ({freeMemberships.length})
        </button>
        <button
          className={`pb-3 cursor-pointer transition-all ${membershipCategory === 'expired' ? 'text-[#2ab391] border-b-2 border-[#2ab391]' : 'text-gray-500 hover:text-gray-800'}`}
          onClick={() => {
            setMembershipCategory('expired');
            setAuditPage(0);
          }}
        >
          Expired Plans ({expiredMemberships.length})
        </button>
      </div>

      {/* Filter by Plan Name/Status (only for Paid Plan category) */}
      {membershipCategory === 'paid' && (
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-6 bg-gray-50 border border-gray-100 p-4 rounded-xl select-none">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mr-1">Filter Paid Plans:</span>

          {/* Desktop view (Pills list) */}
          <div className="hidden md:flex flex-wrap gap-2">
            {[
              { id: 'ALL', label: 'All Paid Plans' },
              { id: 'PROFESSIONAL', label: 'Professional Plan' },
              { id: 'PREMIUM', label: 'Premium Plan' },
              { id: 'YET_TO_EXPIRE', label: 'Yet to Expire (1 Month Remaining)' }
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => {
                  setSelectedPaidFilter(opt.id);
                  setAuditPage(0);
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${selectedPaidFilter === opt.id
                  ? 'bg-[#2ab391] text-white border-[#2ab391] shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-800'
                  }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Mobile view (Dropdown select) */}
          <div className="block md:hidden w-full sm:w-auto">
            <select
              value={selectedPaidFilter}
              onChange={(e) => {
                setSelectedPaidFilter(e.target.value);
                setAuditPage(0);
              }}
              className="w-full sm:w-64 bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs font-semibold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391]"
            >
              <option value="ALL">All Paid Plans</option>
              <option value="PROFESSIONAL">Professional Plan</option>
              <option value="PREMIUM">Premium Plan</option>
              <option value="YET_TO_EXPIRE">Yet to Expire (1 Month Remaining)</option>
            </select>
          </div>
        </div>
      )}

      {/* Record Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
              <th className="p-4 pl-6">Business / Vendor Email</th>
              <th className="p-4">Listing Type</th>
              <th className="p-4">Plan Name</th>
              <th className="p-4">Mobile Number</th>
              {membershipCategory !== 'free' && <th className="p-4">Dates</th>}
              {membershipCategory === 'free' && <th className="p-4">Lead Status</th>}
              <th className="p-4">Status</th>
              {membershipCategory === 'paid' && <th className="p-4">AutoPay</th>}
              {membershipCategory === 'paid' && <th className="p-4 pr-6 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm text-gray-600">
            {displayedAuditList.length === 0 ? (
              <tr>
                <td colSpan={membershipCategory === 'paid' ? 8 : 6} className="p-12 text-center text-gray-400 font-semibold">
                  No records found for the selected category.
                </td>
              </tr>
            ) : (
              displayedAuditList.map(m => {
                const isIndividual = (m.listing_type || m.listingType) === 'INDIVIDUAL';
                return (
                  <tr key={m.id || m.vendor_email} className="hover:bg-gray-50 transition-all border-b border-gray-100 last:border-0">
                    <td className="p-4 pl-6 text-left">
                      <div className="font-semibold text-gray-800">{m.company_name}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{m.vendor_email}</div>
                    </td>
                    <td className="p-4 text-left">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded-md uppercase tracking-wider ${
                        isIndividual
                          ? 'bg-amber-50 text-amber-700 border border-amber-200'
                          : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      }`}>
                        {isIndividual ? 'Individual' : 'Company'}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-0.5 text-[10px] font-extrabold rounded-md ${m.plan_name === 'PREMIUM' ? 'bg-violet-100 text-violet-700' :
                        m.plan_name === 'PROFESSIONAL' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                        {m.plan_name}
                      </span>
                    </td>
                    <td className="p-4 text-sm font-semibold text-gray-700 text-left">
                      {m.phone && m.phone !== 'N/A' ? (
                        <button
                          onClick={() => setRevealedAuditPhone(prev => prev === (m.id || m.vendor_email) ? null : (m.id || m.vendor_email))}
                          className="flex items-center gap-1.5 hover:text-indigo-600 transition-colors duration-150 focus:outline-none cursor-pointer"
                          title={revealedAuditPhone === (m.id || m.vendor_email) ? "Hide phone number" : "Click to view phone number"}
                        >
                          <span className="font-mono text-gray-600 tracking-wider">
                            {revealedAuditPhone === (m.id || m.vendor_email) ? m.phone : maskPhoneNumber(m.phone)}
                          </span>
                          {revealedAuditPhone === (m.id || m.vendor_email) ? (
                            <EyeOff size={14} className="text-gray-400 hover:text-indigo-500" />
                          ) : (
                            <Eye size={14} className="text-gray-400 hover:text-indigo-500" />
                          )}
                        </button>
                      ) : (
                        <span className="text-gray-400 font-normal">N/A</span>
                      )}
                    </td>
                    {membershipCategory !== 'free' && (
                      <td className="p-4 text-xs font-semibold text-gray-700 text-left whitespace-nowrap">
                        {m.start_date ? (
                          <div className="space-y-0.5">
                            <div><span className="font-semibold text-gray-400">Start:</span> <span className="font-semibold text-gray-700">{formatDate(m.start_date)}</span></div>
                            <div><span className="font-semibold text-gray-400">End:</span> <span className="font-semibold text-gray-700">{formatDate(m.end_date)}</span></div>
                          </div>
                        ) : (
                          <span className="text-gray-400 font-normal">No Dates</span>
                        )}
                      </td>
                    )}
                    {membershipCategory === 'free' && (
                      <td className="p-4 text-left">
                        <select
                          value={m.lead_status || 'New'}
                          onChange={(e) => handleLeadStatusChange(m.vendor_email, e.target.value)}
                          className={`border rounded-lg px-2 py-1 text-xs font-bold focus:outline-none focus:ring-1 focus:ring-[#2ab391] cursor-pointer transition-colors ${getLeadStatusColorClass(m.lead_status)}`}
                        >
                          <option value="New" className="bg-white text-blue-600 font-semibold">New</option>
                          <option value="Hot" className="bg-white text-emerald-600 font-semibold">Hot</option>
                          <option value="Warm" className="bg-white text-amber-600 font-semibold">Warm</option>
                          <option value="Cold" className="bg-white text-slate-600 font-semibold">Cold</option>
                          <option value="Lost" className="bg-white text-rose-600 font-semibold">Lost</option>
                          <option value="Architect/Designer" className="bg-white text-purple-600 font-semibold">Architect/Designer</option>
                        </select>
                      </td>
                    )}
                    <td className="p-4 text-left">
                      <span className={`px-2.5 py-1 rounded text-xs font-bold ${m.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' :
                        m.status === 'YET_TO_EXPIRE' ? 'bg-amber-50 text-amber-700 border border-amber-200/50' :
                          'bg-rose-50 text-rose-700'
                        }`}>
                        {m.status === 'YET_TO_EXPIRE' ? 'Yet to Expire (Sales)' : m.status}
                      </span>
                    </td>
                    {/* AutoPay status badge (Only for Paid Plans) */}
                    {membershipCategory === 'paid' && (
                      <td className="p-4 text-left whitespace-nowrap">
                        {m.autopay_status === 'ACTIVE' || m.autopay_status === 'CANCELLED' || m.autopay_status === 'HALTED' || m.auto_pay_enabled ? (
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold inline-block whitespace-nowrap ${
                            m.autopay_status === 'CANCELLED' ? 'bg-red-50 text-red-600 border border-red-200/60' :
                            m.autopay_status === 'HALTED'    ? 'bg-amber-50 text-amber-700 border border-amber-200/60' :
                            'bg-emerald-50 text-emerald-700 border border-emerald-200/60'
                          }`}>
                            {m.autopay_status === 'CANCELLED' ? 'CANCELLED' : m.autopay_status === 'HALTED' ? 'HALTED' : 'ACTIVE'}
                          </span>
                        ) : (
                          <span className="text-[11px] text-gray-400 font-medium">Manual / One-Time</span>
                        )}
                      </td>
                    )}
                    {/* Actions column (Only for Paid Plans) */}
                    {membershipCategory === 'paid' && (
                      <td className="p-4 pr-6 text-right whitespace-nowrap">
                        {m.auto_pay_enabled && m.autopay_status !== 'CANCELLED' ? (
                          <button
                            onClick={() => setCancellingMembership(m)}
                            title="Cancel AutoPay Mandate"
                            className="px-2.5 py-1 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 text-red-600 font-bold text-[11px] transition-colors cursor-pointer inline-flex items-center gap-1 shrink-0"
                          >
                            <XCircle size={13} />
                            <span>Cancel AutoPay</span>
                          </button>
                        ) : m.autopay_status === 'CANCELLED' ? (
                          <span className="text-[11px] text-gray-400 italic">AutoPay Cancelled</span>
                        ) : (
                          <span className="text-[11px] text-gray-400 font-medium">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-gray-500 font-medium border-t border-gray-100 pt-5 mt-6 select-none bg-gray-50/40">
        <div className="text-gray-500 text-xs font-semibold text-center md:text-left w-full md:w-auto">
          {activeList.length === 0 ? (
            'No records available'
          ) : (
            `Showing ${safeAuditPage * auditPageSize + 1} to ${Math.min((safeAuditPage + 1) * auditPageSize, activeList.length)} of ${activeList.length} entries`
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full md:w-auto">
          <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => fetchMemberships()}
              title="Reload current page"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 hover:text-[#2ab391] hover:border-[#2ab391]/40 hover:bg-teal-50/50 rounded-xl font-bold text-xs transition-all cursor-pointer shadow-sm"
            >
              <RotateCw size={12} />
              Reload
            </button>
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Show</span>
            <div className="relative">
              <select
                value={auditPageSize}
                onChange={(e) => {
                  setAuditPageSize(Number(e.target.value));
                  setAuditPage(0);
                }}
                className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-sm"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Entries</span>
          </div>

          <div className="flex items-center justify-center gap-1 w-full sm:w-auto">
            <button
              onClick={() => setAuditPage(p => Math.max(0, p - 1))}
              disabled={safeAuditPage === 0}
              className="px-3 py-1.5 border border-gray-200 text-gray-600 rounded-xl font-semibold hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all shadow-xs"
            >
              Previous
            </button>

            {getPageNumbers(safeAuditPage, auditTotalPages).map((p, idx) => {
              if (typeof p === 'string') {
                return (
                  <span key={p + idx} className="px-2 text-gray-400">...</span>
                );
              }

              return (
                <button
                  key={p}
                  onClick={() => setAuditPage(p)}
                  className={`w-8 h-8 rounded-xl text-xs font-bold cursor-pointer transition-all ${safeAuditPage === p
                    ? 'bg-[#2ab391] text-white shadow-xs'
                    : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  {p + 1}
                </button>
              );
            })}

            <button
              onClick={() => setAuditPage(p => Math.min(auditTotalPages - 1, p + 1))}
              disabled={safeAuditPage >= auditTotalPages - 1 || auditTotalPages === 0}
              className="px-3 py-1.5 border border-gray-200 text-gray-600 rounded-xl font-semibold hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all shadow-xs"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Cancel AutoPay Confirmation Modal */}
      {cancellingMembership && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 text-left space-y-4 transform transition-all">
            <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
              <div className="w-10 h-10 rounded-full bg-red-50 border border-red-200 flex items-center justify-center shrink-0">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Cancel Membership AutoPay</h3>
                <p className="text-xs text-gray-500 font-medium">Stop future automatic renewals for this vendor.</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-3.5 space-y-2 text-xs border border-gray-150">
              <div className="flex justify-between border-b border-gray-200/60 pb-1.5">
                <span className="text-gray-500 font-semibold">Vendor Email:</span>
                <span className="font-bold text-gray-900">{cancellingMembership.vendor_email}</span>
              </div>
              <div className="flex justify-between border-b border-gray-200/60 pb-1.5">
                <span className="text-gray-500 font-semibold">Company Name:</span>
                <span className="font-bold text-gray-900">{cancellingMembership.company_name}</span>
              </div>
              <div className="flex justify-between border-b border-gray-200/60 pb-1.5">
                <span className="text-gray-500 font-semibold">Current Plan:</span>
                <span className="font-bold text-violet-700">{cancellingMembership.plan_name}</span>
              </div>
              <div className="flex justify-between border-b border-gray-200/60 pb-1.5">
                <span className="text-gray-500 font-semibold">Razorpay Sub ID:</span>
                <span className="font-mono font-bold text-gray-900 break-all">{cancellingMembership.razorpay_subscription_id || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 font-semibold">AutoPay Status:</span>
                <span className="font-bold text-emerald-600">{cancellingMembership.autopay_status || 'ACTIVE'}</span>
              </div>
            </div>

            <p className="text-xs text-gray-600 font-medium leading-relaxed">
              Are you sure you want to cancel the membership AutoPay subscription for <strong>{cancellingMembership.vendor_email}</strong>?
              This will cancel the mandate on Razorpay and stop future automatic yearly debits for their {cancellingMembership.plan_name} plan.
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setCancellingMembership(null)}
                disabled={cancellingLoading}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-xl transition-colors cursor-pointer disabled:opacity-50"
              >
                No, Keep Active
              </button>
              <button
                onClick={handleConfirmCancelAutoPay}
                disabled={cancellingLoading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white text-xs font-bold rounded-xl transition-colors shadow-xs cursor-pointer flex items-center gap-1.5 font-bold"
              >
                {cancellingLoading ? <RefreshCw size={13} className="animate-spin" /> : null}
                <span>{cancellingLoading ? 'Cancelling...' : 'Yes, Cancel AutoPay'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipAuditTab;
